```vue
<template>
  <div class="container my-5">

    <!-- Page Header -->
    <div class="wishlist-header mb-4">

      <h1>
        My Wishlist 
      </h1>

      <p>
        Books you saved for later
      </p>

    </div>


    <!-- Empty Wishlist -->
    <div
      v-if="wishlist.length === 0"
      class="empty-wishlist text-center"
    >

      <div class="empty-icon">
        ♡
      </div>

      <h3>
        Your wishlist is empty
      </h3>

      <p>
        You haven't added any books yet.
      </p>

      <router-link
        to="/books"
        class="btn btn-primary"
      >
        Browse Books
      </router-link>

    </div>


    <!-- Wishlist Books -->
    <div
      v-else
      class="wishlist-list"
    >

      <div
        v-for="book in wishlist"
        :key="book.ISBN"
        class="wishlist-item"
      >

        <!-- Book Image -->
        <div class="wishlist-image-wrapper">

          <img
            :src="book.image"
            :alt="book.name"
            class="wishlist-image"
          >

        </div>


        <!-- Book Information -->
        <div class="wishlist-info">

          <span class="category">
            {{ book.category }}
          </span>

          <h3>
            {{ book.name }}
          </h3>

          <p class="author">
            by {{ book.author }}
          </p>

          <p class="price">
            {{ formatPrice(book.price) }}
          </p>

          <p class="book-meta">
            {{ book.pages }} pages
            · ISBN: {{ book.ISBN }}
          </p>


          <!-- Actions -->
          <div class="wishlist-actions">

            <!-- Remove -->
            <button
              class="btn btn-outline-danger"
              @click="removeFromWishlist(book)"
            >
              🗑️ Remove
            </button>


            <!-- Add to Cart -->
            <button
              class="btn"
              :class="
                isInCart(book)
                  ? 'btn-success'
                  : 'btn-primary'
              "
              @click="addToCart(book)"
            >
              {{
                isInCart(book)
                  ? '🛒 Added to Cart'
                  : '🛒 Add to Cart'
              }}
            </button>


            <!-- View Details -->
            <router-link
              :to="'/books/' + book.ISBN"
              class="btn btn-info text-white"
            >
              👁️ View Details
            </router-link>

          </div>

        </div>

      </div>

    </div>

  </div>
</template>


<script>
export default {
  name: 'Wishlist',

  computed: {

    wishlist() {
      return this.$store.state.wishlist
    }

  },


  methods: {

    /* -------------------------
       Format Price
    ------------------------- */

    formatPrice(value) {

      return new Intl.NumberFormat(
        'en-SA',
        {
          style: 'currency',
          currency: 'SAR'
        }
      ).format(value)
    },


    /* -------------------------
       Remove from Wishlist
    ------------------------- */

    removeFromWishlist(book) {

      this.$store.dispatch(
        'removeFromWishlist',
        book
      )
    },


    /* -------------------------
       Check Cart
    ------------------------- */

    isInCart(book) {

      return this.$store.getters.isInCart(
        book.ISBN
      )
    },


    /* -------------------------
       Add to Cart
    ------------------------- */

    addToCart(book) {

      this.$store.dispatch(
        'addToCart',
        book
      )
    }

  }
}
</script>


<style scoped>

.wishlist-header {
  text-align: center;
}


.wishlist-header h1 {
  color: var(--text);

  font-weight: 700;

  margin-bottom: 8px;
}


.wishlist-header p {
  color: var(--text-secondary);

  margin: 0;
}


/* =========================
   Wishlist List
========================= */

.wishlist-list {
  display: flex;

  flex-direction: column;

  gap: 20px;
}


/* =========================
   Wishlist Item
========================= */

.wishlist-item {
  display: flex;

  align-items: center;

  gap: 25px;

  padding: 20px;

  background: var(--surface);

  border-radius: 18px;

  border: 1px solid var(--border);

  box-shadow:
    0 6px 20px
    rgba(108, 59, 255, 0.07);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}


.wishlist-item:hover {
  transform:
    translateY(-3px);

  box-shadow:
    0 12px 28px
    rgba(108, 59, 255, 0.12);
}


/* =========================
   Image
========================= */

.wishlist-image-wrapper {
  width: 150px;

  height: 210px;

  flex-shrink: 0;

  display: flex;

  justify-content: center;

  align-items: center;

  background: var(--surface);

  border-radius: 12px;

  overflow: hidden;
}


.wishlist-image {
  width: 100%;

  height: 100%;

  object-fit: contain;
}


/* =========================
   Information
========================= */

.wishlist-info {
  flex: 1;
}


.category {
  display: inline-block;

  padding: 5px 10px;

  border-radius: 20px;

  background: #ede9fe;

  color: #6d28d9;

  font-size: 0.75rem;

  font-weight: 700;

  margin-bottom: 8px;
}


.wishlist-info h3 {
  color: var(--text);

  font-size: 1.5rem;

  font-weight: 700;

  margin-bottom: 5px;
}


.author {
  color: var(--text-secondary);

  margin-bottom: 10px;
}


.price {
  color: var(--primary);

  font-size: 1.2rem;

  font-weight: 700;

  margin-bottom: 8px;
}


.book-meta {
  color: var(--text-secondary);

  font-size: 0.85rem;

  margin-bottom: 18px;
}


/* =========================
   Buttons
========================= */

.wishlist-actions {
  display: flex;

  flex-wrap: wrap;

  gap: 10px;
}


.wishlist-actions .btn {
  border-radius: 9px;

  font-weight: 600;
}


/* =========================
   Empty Wishlist
========================= */

.empty-wishlist {
  padding: 70px 20px;

  background: var(--surface);

  border-radius: 20px;

  border: 1px solid var(--border);
}


.empty-icon {
  font-size: 4rem;

  color: var(--primary);

  margin-bottom: 15px;
}


.empty-wishlist h3 {
  color: var(--text);

  font-weight: 700;

  margin-bottom: 10px;
}


.empty-wishlist p {
  color: var(--text-secondary);

  margin-bottom: 20px;
}


.empty-wishlist .btn {
  border-radius: 10px;

  font-weight: 600;
}


/* =========================
   Mobile
========================= */

@media (max-width: 768px) {

  .wishlist-item {
    flex-direction: column;

    align-items: stretch;

    text-align: center;
  }


  .wishlist-image-wrapper {
    width: 180px;

    height: 240px;

    margin: auto;
  }


  .wishlist-actions {
    justify-content: center;
  }


  .wishlist-actions .btn {
    flex: 1;

    min-width: 140px;
  }

}

</style>
```
